import React from "react"
import { Box, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography } from "@material-ui/core"
import { ProjectSelector } from "../ProjectSelector"
import { TaskSelector } from "../TaskSelector/TaskSelector"
import { ReportedHourViewModel } from "../../api/reported-hours"
import { useHourReportMutations } from "../../hooks/use-hour-report-mutations"
import { useHourReportFormOptions } from "../../hooks/use-form-options"
import { X } from "react-feather"
import { useDebounce } from "../../../hooks/use-debounce"
import ManualEntryFields from "../ManualEntryFields"
import IntervalEntryFields from "../IntervalEntryFields"
import { convertHoursAndMinutesToDecimalHours, getHHmmFromDate } from "../../utils"
import HourReportActionButton from "../HourReportActionButton"
import type { ManualEntryValue } from "../ManualEntryFields"
import { useTranslation } from "react-i18next"

type HourReportEditFormProps = {
  setEditingReportId: (reportId: string | null) => void
  report: ReportedHourViewModel | undefined
  supportsSubmission?: boolean
}

type HourReportUpdate =
  | { description: string | null }
  | { projectId: string | null }
  | { taskId: string | null }
  | { hours: number }
  | { startTime: string; endTime: string }

export default function HourReportEditForm({
  setEditingReportId,
  report,
  supportsSubmission = true,
}: HourReportEditFormProps) {
  const { t } = useTranslation("hour-report")
  const { projectOptions } = useHourReportFormOptions()
  const {
    updateHourReportInfo,
    updateHourReportHours,
    updateHourReportProject,
    updateHourReportTask,
    performHourReportAdminAction,
  } = useHourReportMutations()

  const updateHourReport = React.useCallback(
    (report: ReportedHourViewModel, changes: HourReportUpdate) => {
      if (report.editableAs === "hour-report-admin") {
        return performHourReportAdminAction(report.id, { action: "update", changes })
      }

      if ("description" in changes) {
        return updateHourReportInfo(report.id, { description: changes.description })
      }

      if ("projectId" in changes) {
        return updateHourReportProject(report.id, { projectId: changes.projectId })
      }

      if ("taskId" in changes) {
        return updateHourReportTask(report.id, { taskId: changes.taskId })
      }

      if ("hours" in changes) {
        return updateHourReportHours(report.id, { hours: changes.hours })
      }

      return updateHourReportHours(report.id, changes)
    },
    [
      performHourReportAdminAction,
      updateHourReportHours,
      updateHourReportInfo,
      updateHourReportProject,
      updateHourReportTask,
    ]
  )

  const updateDescription = React.useCallback(
    (report: ReportedHourViewModel, description: string | null) => updateHourReport(report, { description }),
    [updateHourReport]
  )

  const { debouncedFn: debouncedUpdateDescription, flush: flushThrottledUpdate } = useDebounce(updateDescription, 500, {
    leading: true,
    trailing: true,
    flushOnUnmount: true,
  })

  const flushPendingUpdates = React.useCallback(() => {
    flushThrottledUpdate()
  }, [flushThrottledUpdate])

  const handleClose = () => {
    flushPendingUpdates()
    setEditingReportId(null)
  }

  const handleProjectChange = (projectId: string | null) => {
    if (!report) return
    updateHourReport(report, { projectId })
  }

  const handleTaskChange = (taskId: string | null) => {
    if (!report) return
    updateHourReport(report, { taskId })
  }

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!report) return
    const description = event.target.value.trim()
    debouncedUpdateDescription(report, description || null)
  }

  const handleMarkAsReadyToCheck = () => {
    if (!report) return
    if (report.editableAs === "hour-report-admin") {
      return performHourReportAdminAction(report.id, { action: "submit" })
    }
    updateHourReportInfo(report.id, { status: "submitted" })
  }

  const handleManualDurationChange = (value: ManualEntryValue) => {
    if (!report?.isReportedManually) return
    const hours = convertHoursAndMinutesToDecimalHours(value.hours, value.minutes)
    return updateHourReport(report, { hours })
  }

  const handleStartChange = (start: Date | null) => {
    if (!report || report?.isReportedManually || !start) return
    const startTime = getHHmmFromDate({ date: start, midnightAs24Hour: false })
    updateHourReport(report, { startTime, endTime: report.endTime! })
  }

  const handleEndChange = (end: Date | null) => {
    if (!report || report.isReportedManually || !end) return
    const endTime = getHHmmFromDate({ date: end, midnightAs24Hour: true })
    updateHourReport(report, { endTime, startTime: report.startTime! })
  }

  React.useEffect(() => {
    window.addEventListener?.("beforeunload", flushPendingUpdates)

    return () => {
      window.removeEventListener?.("beforeunload", flushPendingUpdates)
    }
  }, [flushPendingUpdates])

  if (!report) return null

  const isReportDraft = report?.status === "open"

  return (
    <>
      <Dialog onClose={handleClose} open={report !== undefined} maxWidth="sm" fullWidth>
        <Box style={{ position: "absolute", right: 16, top: 8, zIndex: 1 }}>
          <IconButton edge="end" color="inherit" onClick={handleClose}>
            <X size={20} />
          </IconButton>
        </Box>
        <DialogTitle>{t("dialogs.editTitle", { defaultValue: "Edit hour report" })}</DialogTitle>

        <DialogContent style={{ paddingBottom: 24 }}>
          <ProjectSelector
            selectedProjectId={report.projectId}
            onChange={handleProjectChange}
            projectOptions={projectOptions}
          />
          {report.projectId ? (
            <TaskSelector projectId={report.projectId} selectedTaskId={report.taskId} onChange={handleTaskChange} />
          ) : null}
          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>
              {t("form.description", { defaultValue: "Description" })}
            </Typography>
            <TextField
              name="description"
              defaultValue={report.description || ""}
              onChange={handleDescriptionChange}
              variant="outlined"
              margin="dense"
              minRows={2}
              multiline
              fullWidth
            />
          </Box>

          {report.isReportedManually ? (
            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom>
                {t("form.timeUsed", { defaultValue: "Time used" })}
              </Typography>

              <ManualEntryFields
                key={report.id}
                hours={report.durationInHoursAndMinutes.hours}
                minutes={report.durationInHoursAndMinutes.minutes}
                onChange={handleManualDurationChange}
                hoursLabel={t("form.hours", { defaultValue: "Hours" })}
                minutesLabel={t("form.minutes", { defaultValue: "Minutes" })}
              />
            </Box>
          ) : null}

          {report.isReportedByInterval || report.isReportedByTimer ? (
            <Box mt={3}>
              <IntervalEntryFields
                date={report.date}
                start={report.startTime!}
                end={report.endTime!}
                onStartChange={handleStartChange}
                onEndChange={handleEndChange}
                startLabel={t("form.startTime", { defaultValue: "Start Time" })}
                endLabel={t("form.endTime", { defaultValue: "End Time" })}
                durationDescription={(hours, minutes) =>
                  t("form.duration", { defaultValue: "Time used: {{hours}}h {{minutes}}m", hours, minutes })
                }
                isClearable={false}
              />
            </Box>
          ) : null}

          {isReportDraft && supportsSubmission ? (
            <Box my={3}>
              <HourReportActionButton
                icon="send"
                color="primary"
                variant="contained"
                onClick={handleMarkAsReadyToCheck}
              >
                Submit report for check
              </HourReportActionButton>
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}
